import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {collectEnumValues} from '../../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {getHasHighlightAlgorithms} from '../../../../store/featureFlags/featureFlagsSelectors';
import {getCellsSearchHighlightAlgorithm} from '../../../../store/panels/cells/search/cellsPanelSearchSelectors';
import {HighlightAlgorithmsEnum} from '../../../../store/panels/cells/search/cellsPanelSearchState';
import {StyledSearchBarRightButton} from '../../../common/searchBar/searchBar';

/*
 * Constants.
 */

const highlightAlgorithmsEnumValues = collectEnumValues(HighlightAlgorithmsEnum);

const highlightAlgorithmsLabels: Record<HighlightAlgorithmsEnum, ReactNode> = {
  [HighlightAlgorithmsEnum.BUMP_HIGHLIGHT_LENGTH]: (
    <Trans id="highlight-algo-bump-highlight-length">Bump highlight length</Trans>
  ),
  [HighlightAlgorithmsEnum.DEFAULT]: <Trans id="highlight-algo-default">Default</Trans>,
  [HighlightAlgorithmsEnum.TRIM_CHARS_MULTIPLIER_3]: (
    <Trans id="highlight-algo-trim-chars-multiplier-3">Trim chars with multiplier (subject variation)</Trans>
  ),
};

/*
 * Props.
 */

interface InboxesSearchHighlightsAlgorithmProps {
  hasFocus: boolean;
  onHighlightAlgorithmSelect: HandlerOf<HighlightAlgorithmsEnum>;
}

/*
 * Component.
 */

export const InboxesSearchHighlightsAlgorithm: FC<InboxesSearchHighlightsAlgorithmProps> = (props) => {
  const {hasFocus, onHighlightAlgorithmSelect} = props;

  const theme = useUserTheme();

  const highlightAlgorithm = useAppSelector(getCellsSearchHighlightAlgorithm);
  const selectedHighlightAlgorithm = highlightAlgorithm ?? HighlightAlgorithmsEnum.DEFAULT;

  const hasHighlightAlgorithms = useAppSelector(getHasHighlightAlgorithms);

  if (!hasHighlightAlgorithms) {
    return null;
  }

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => (
        <StyledSearchBarRightButton>
          <Icon
            name="preferencesSmall"
            color={hasFocus ? theme.greys.shade70 : theme.greys.white}
            hoverColor={hasFocus ? theme.greys.shade80 : theme.greys.shade30}
          />
        </StyledSearchBarRightButton>
      )}
      renderModal={(popoverProps) => (
        <SimpleDropdown {...popoverProps}>
          {highlightAlgorithmsEnumValues.map((algo) => (
            <DropdownItem
              key={algo}
              isChecked={selectedHighlightAlgorithm === algo}
              onSelect={() => onHighlightAlgorithmSelect(algo)}
              shouldReserveRightIcon
            >
              <DropdownItemContent isChecked={selectedHighlightAlgorithm === algo}>
                {highlightAlgorithmsLabels[algo]}
              </DropdownItemContent>
            </DropdownItem>
          ))}
        </SimpleDropdown>
      )}
    />
  );
};
